import type { ImageParams } from 'components/Common/ResponsiveImage'
import type { ImageChangeHandler, ImageLoadHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import type { PropsWithChildren, ReactElement } from 'react'
import { useCallback, useContext } from 'react'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import type { SearchTileDensity } from './searchTileDensity'
import SearchImageCarouselWrapper from './SearchImageCarouselWrapper'
import SearchTileImageMosaic from './SearchTileImageMosaic'

const imageParams: ImageParams = {
  quality: 'best',
  fit: 'center',
  mobileWidth: '578px',
  tabletWidth: '670px',
  desktopWidth: '410px',
  dpr: 2,
}

interface Props extends PropsWithChildren {
  bookmarkAction?: ReactElement
  images: Array<App.Image>
  productLabel?: ReactElement | false
  bottomLeft?: ReactElement
  eagerLoadFirstImage?: boolean
  onImageChange?: ImageChangeHandler
  onImageLoad?: ImageLoadHandler
  density?: SearchTileDensity
  showImageMosaic?: boolean
  className?: string
}

function _SearchTileHero({
  bookmarkAction,
  images,
  productLabel,
  bottomLeft,
  eagerLoadFirstImage,
  onImageChange,
  onImageLoad,
  density = 'default',
  showImageMosaic = false,
  className,
}: Props) {
  // oxlint-disable-next-line react-hooks/rules-of-hooks
  const dispatchOfferListEvent = useContext(OfferListEventsContext)

  // oxlint-disable-next-line react-hooks/rules-of-hooks
  const handleImageLoaded = useCallback<ImageLoadHandler>((index, image) => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
    onImageLoad?.(index, image)
  }, [dispatchOfferListEvent, onImageLoad])

  if (density === 'cro-compact' && showImageMosaic) {
    return <SearchTileImageMosaic
      images={images}
      productLabel={productLabel}
      eagerLoadFirstImage={eagerLoadFirstImage}
      onImageLoad={handleImageLoaded}
    />
  }

  return <SearchImageCarouselWrapper
    // Reset scroll and hover state when rebucketing changes the lead image.
    key={images[0]?.id ?? images[0]?.url}
    className={className}
    productLabel={productLabel}
    bottomLeft={bottomLeft}
    bookmarkAction={bookmarkAction ? <TripGuard>{bookmarkAction}</TripGuard> : undefined}
    images={images}
    imageParams={imageParams}
    eagerLoadFirstImage={eagerLoadFirstImage}
    onImageChange={onImageChange}
    onImageLoad={handleImageLoaded}
  />
}

export default _SearchTileHero
